import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchUsers } from '@/lib/api/users';
import { useDepartments } from '@/entities/department';

import type { RoleDepartment } from './RRolePicker';

/**
 * Отделы со списком сотрудников для «Добавить всех».
 * Имена берём из /departments, состав — из `departmentIds` пользователей
 * (тот же запрос `all-users`, что и у формы, поэтому лишней сетевой работы нет).
 */
export function useRoleDepartments(): RoleDepartment[] {
  const { data: usersData } = useQuery({
    queryKey: ['all-users'],
    queryFn: () => fetchUsers(''),
    staleTime: 5 * 60 * 1000,
  });
  const { data: deptData } = useDepartments();

  return useMemo(() => {
    const membersByDept = new Map<number, number[]>();
    for (const u of usersData?.result ?? []) {
      if (String(u.Active ?? 'Y') !== 'Y') {
        continue;
      }
      const ids =
        u.departmentIds && u.departmentIds.length > 0
          ? u.departmentIds
          : u.DepartmentID
            ? [u.DepartmentID]
            : [];
      for (const d of ids) {
        const arr = membersByDept.get(d) ?? [];
        arr.push(u.ID);
        membersByDept.set(d, arr);
      }
    }

    return (deptData?.result ?? [])
      .map((d) => ({
        id: d.id,
        name: String(d.name ?? '').trim(),
        memberIds: membersByDept.get(d.id) ?? [],
      }))
      .filter((d) => d.name && d.memberIds.length > 0);
  }, [usersData, deptData]);
}
